import React from "react";
import StatTile from "../../components/StatTile.jsx";

/* Busy and free hours for the month on screen — the same month, filters
   and rows the timeline above is drawing.

   Every figure here is the API's (GET /classroom-management/schedule/month
   returns `hours` per row and for the whole month). Nothing is added up in
   the browser: a total worked out here and a total worked out there is how
   two screens end up quoting different numbers for the same month.

   WHAT "FREE" MEANS, stated because it is a choice and not a fact:
   capacity is the house standard working day (STANDARD_DAILY_HOURS, the
   same denominator the reports screen divides by for lab utilization)
   times the days the day-filter lets through. Free is what is left of
   that after the booked sessions, floored at zero — a room booked past
   the standard day is fully used, not negatively free. */

const hoursText = (n) => `${n ?? 0} h`;

const TimelineHours = ({ hours, rows = [], kind }) => {
  if (!hours) return null;

  const noun = kind === "trainers" ? "Trainer" : "Lab";
  // Busiest first: the question this section answers is "who is stretched
  // and who is idle", and that reads top-down.
  const ranked = [...rows]
    .filter((r) => r?.hours)
    .sort((a, b) => (b.hours.busy || 0) - (a.hours.busy || 0));

  /* .br-chart is the app's own section panel -- the same frame
     ClassroomCharts wraps every chart in -- so this reads as one more
     panel on the tab rather than a block with its own look.

     A plain block comment rather than a JSX one: a JSX comment is only
     valid as a CHILD, and in expression position just inside `return (`
     it is a syntax error. */
  return (
    <section className="br-chart" aria-label="Busy and free hours">
      <header className="br-chart-head">
        <div>
          <h5 className="br-chart-title">Busy and free hours</h5>
          <p className="br-chart-sub">
            Across {hours.days} day{hours.days === 1 ? "" : "s"} in this month, at{" "}
            {hours.perDay} working hours a day.
          </p>
        </div>
      </header>

      <div className="br-stats">
        <StatTile num={hoursText(hours.busy)} label="Total busy" variant="busy" />
        <StatTile num={hoursText(hours.free)} label="Total free" variant="free" />
        <StatTile num={hoursText(hours.capacity)} label="Total capacity" />
      </div>

      {ranked.length === 0 ? (
        <p className="pgm-empty">Nothing is scheduled in this month.</p>
      ) : (
        <div className="pgm-tablescroll">
          <table className="pgm-table pgm-table--values">
            <thead>
              <tr>
                <th className="pgm-num">#</th>
                <th>{noun}</th>
                <th>Busy</th>
                <th>Free</th>
                <th>Capacity</th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((r, i) => (
                <tr key={r.key || `${r.label}-${i}`}>
                  <td className="pgm-num">{i + 1}</td>
                  <td>
                    <span className="pgm-person-id">
                      <span className="pgm-person-name">{r.label}</span>
                      {r.meta && <span className="pgm-person-mail">{r.meta}</span>}
                    </span>
                  </td>
                  <td className="pgm-tel">{hoursText(r.hours.busy)}</td>
                  <td className="pgm-tel">{hoursText(r.hours.free)}</td>
                  <td className="pgm-tel">{hoursText(r.hours.capacity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default TimelineHours;
